import * as React from "react";
import { Input } from "@/components/ui/input";

// Keeps digits and a single decimal point (max 2 decimals), plus a leading
// minus when allowed: "1,500,000.5" -> "1500000.5".
const toRaw = (text, allowNegative = false) => {
  const str = String(text ?? "");
  const sign = allowNegative && str.trim().startsWith("-") ? "-" : "";
  const [int, ...rest] = str.replace(/[^\d.]/g, "").split(".");
  return sign + (rest.length ? `${int}.${rest.join("").slice(0, 2)}` : int);
};

// "1500000.5" -> "1,500,000.5" -- a trailing "." or "0" the user is still
// typing is kept, so the text never jumps under their cursor.
export const formatMoneyText = (raw, allowNegative = false) => {
  const clean = toRaw(raw, allowNegative);
  const sign = clean.startsWith("-") ? "-" : "";
  const [int, dec] = clean.slice(sign.length).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return sign + (dec === undefined ? grouped : `${grouped}.${dec}`);
};

// Drop-in for <Input type="number"> on money fields: shows thousands
// separators while typing, but onChange still receives the plain number
// string in e.target.value ("1500000"), so callers and the API are unchanged.
export const MoneyInput = React.forwardRef(({ value, onChange, allowNegative = false, ...props }, forwardedRef) => {
  const innerRef = React.useRef(null);
  const caret = React.useRef(null); // how many digits sat left of the caret
  React.useImperativeHandle(forwardedRef, () => innerRef.current);

  const display = formatMoneyText(value, allowNegative);

  // Commas are inserted/removed on every keystroke, which would otherwise
  // throw the caret to the end -- put it back after the same digit instead.
  React.useLayoutEffect(() => {
    const el = innerRef.current;
    if (caret.current === null || !el || document.activeElement !== el) return;
    let seen = 0, pos = 0;
    while (pos < display.length && seen < caret.current) {
      if (/[\d.-]/.test(display[pos])) seen += 1;
      pos += 1;
    }
    el.setSelectionRange(pos, pos);
    caret.current = null;
  }, [display]);

  const handleChange = (e) => {
    const { value: text, selectionStart } = e.target;
    caret.current = text.slice(0, selectionStart ?? text.length).replace(/[^\d.-]/g, "").length;
    onChange?.({ target: { value: toRaw(text, allowNegative) } });
  };

  return <Input {...props} ref={innerRef} type="text" inputMode="decimal" autoComplete="off" value={display} onChange={handleChange} />;
});
MoneyInput.displayName = "MoneyInput";
